import type { Schema } from '@tiptap/pm/model';
import MarkdownIt from 'markdown-it';
import { MarkdownParser, defaultMarkdownParser } from 'prosemirror-markdown';

import { documentPlugin } from '../plugins/document';
import { mentionPlugin } from '../plugins/mention';
import { strikePlugin } from '../plugins/strike';
import { underlinePlugin } from '../plugins/underline';

const md = new MarkdownIt({
  html: true,
  breaks: true,
  typographer: true,
  linkify: true,
})
  .disable('strikethrough')
  .disable('table');

md.use(mentionPlugin);
md.use(documentPlugin);
md.use(underlinePlugin);
md.use(strikePlugin);

const { image: _, ...defaultTokens } = defaultMarkdownParser.tokens;

/**
 * Parses a custom node in the Markdown parser.
 *
 * @param name - The name of the custom node.
 * @param type - The type of the custom node.
 * @param update - A function to update the attributes of the custom node.
 * @returns The parsed custom node.
 */
const parseCustomNode = ({
  name,
  type = 'node',
  update = (attrs) => attrs,
}: {
  name: string;
  type?: 'node' | 'mark' | 'block';
  update?: (attrs: { [key: string]: any }) => { [key: string]: any };
}): MarkdownParser['tokens']['number'] => {
  return {
    [type]: name,
    getAttrs: (token) => {
      const attrsString = token.content;

      const attrs = Object.fromEntries(
        attrsString.split(',').map((attr) => {
          const [key, value] = attr.split(':');

          const originalValue = value
            .replace(/\\u002E/g, '.')
            .replace(/\\u002C/g, ',')
            .replace(/\\u007B/g, '{')
            .replace(/\\u007D/g, '}')
            .replace(/\\u003A/g, ':');

          return [key.trim(), originalValue.trim()];
        })
      );

      return update(attrs);
    },
    noCloseToken: true,
  };
};

export const getParser = (schema: Schema) =>
  new MarkdownParser(schema, md, {
    ...defaultTokens,
    bullet_list: {
      ...defaultTokens.bullet_list,
      block: 'bulletList',
    },
    ordered_list: {
      ...defaultTokens.ordered_list,
      block: 'orderedList',
    },
    list_item: {
      ...defaultTokens.list_item,
      block: 'listItem',
    },
    code_block: {
      ...defaultTokens.code_block,
      block: 'codeBlock',
    },
    fence: {
      ...defaultTokens.fence,
      block: 'codeBlock',
    },
    hr: {
      ...defaultTokens.hr,
      node: 'horizontalRule',
    },
    hardbreak: {
      ...defaultTokens.hardbreak,
      node: 'hardBreak',
    },
    em: {
      ...defaultTokens.em,
      mark: 'italic',
    },
    strong: {
      ...defaultTokens.strong,
      mark: 'bold',
    },
    underline: {
      mark: 'underline',
    },
    strike: {
      mark: 'strike',
    },
    mention: parseCustomNode({ name: 'mention' }),
    document: parseCustomNode({
      name: 'document',
      type: 'node',
      update: (attrs) => ({
        ...attrs,
        is_memo: attrs.is_memo === 'true',
      }),
    }),
  });
